import {
	Button,
	SessionCard,
	SessionCardSkeleton,
	SlidingTabs,
} from '@/components/ui';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import {
	RadioButtonGroup,
	RadioOption,
} from '@/components/ui/RadioButtonGroup';
import { useGetSessions, useCreateSession } from '@/services';
import { getUserData } from '@/utils/cookies';
import { useState, useEffect, useRef } from 'react';
import { Calendar, Plus } from 'lucide-react';
import { DataTablePagination } from '@/components/data-table/Pagination';
import DateRangePicker from '@/components/ui/DateRangePicker';
import { useModal } from '@/hooks';
import { PaymentContainer } from '../components';
import { ReturnSessionModal } from '@/pages/admin/sessions/components/Return';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { encodeUrl, formatProfessionalFullName } from '@/utils';
import { E2E_USER_HOME } from '@/constants/e2eSchedulingTestIds';

const tabs = [
	{ id: 'next', label: 'PRÓXIMAS', status: 'CREATED' },
	{ id: 'all', label: 'FILTROS', status: undefined },
];

const UserMinhasConsultas = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { openModal, setOnConfirm, setConfirmText, setCancelText } =
		useModal();
	const openFiltersTab = searchParams.get('tab') === 'filtros';
	const [activeTab, setActiveTab] = useState(
		openFiltersTab ? tabs[1].id : tabs[0].id,
	);

	useEffect(() => {
		if (searchParams.get('tab') === 'filtros') {
			setActiveTab('all');
		}
	}, [searchParams]);

	const [allFilter, setAllFilter] = useState<Sessions.SessionStatus | null>(
		null,
	);

	const activeTabConfig = tabs.find((tab) => tab.id === activeTab);
	const [startDateFilter, setStartDateFilter] = useState<string | null>(null);
	const [endDateFilter, setEndDateFilter] = useState<string | null>(null);
	const [filterByDate, setFilterByDate] = useState(false);
	const [clearDateFilter, _setClearDateFilter] = useState(false);
	const [page, setPage] = useState(1);
	const [selectedReturnDate, setSelectedDate] = useState({
		available_date_id: '',
		is_online: true,
		selected_date: '',
	});
	const selectedReturnDateRef = useRef(selectedReturnDate);
	const limit = 8;

	useEffect(() => {
		selectedReturnDateRef.current = selectedReturnDate;
	}, [selectedReturnDate]);

	const { data: sessions, isLoading } = useGetSessions({
		isMe: true,
		limit,
		page,
		...(activeTab === 'all' && allFilter && { status: allFilter }),
		...(filterByDate &&
			startDateFilter &&
			endDateFilter && {
				date_a: startDateFilter,
				date_b: endDateFilter,
			}),
		...(activeTab === 'next' && {
			statusList: ['CREATED', 'CONFIRMED', 'STARTED'],
		}),
	});


	const allFilters: RadioOption<Sessions.SessionStatus | null>[] = [
		{ value: null, label: 'Todas' },
		{ value: 'CREATED', label: 'Aguardando pagamento' },
		{ value: 'CONFIRMED', label: 'Confirmada' },
		{ value: 'STARTED', label: 'Iniciada' },
		{ value: 'FINISHED', label: 'Finalizada' },
		{ value: 'CANCELLED', label: 'Cancelada' },
	];

	const handleDateChange = (startDate: Date | null, endDate: Date | null) => {
		const startISO = startDate ? startDate.toISOString() : null;
		const endISO = endDate ? endDate.toISOString() : null;
		setPage(1);
		setStartDateFilter(startISO);
		setEndDateFilter(endISO);
		setFilterByDate(!!(startISO && endISO));
	};

	const handleStatusChange = (value: Sessions.SessionStatus | null) => {
		setPage(1);
		setAllFilter(value);
	};

	const sessionsContainerRef = useRef<HTMLDivElement>(null);
	const handlePageChange = (newPage: number) => {
		sessionsContainerRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
		setPage(newPage);
	};

	const { mutate: mutateCreate } = useCreateSession((session) =>
		openModal({
			content: <PaymentContainer session={session} />,
			title: 'Realizar pagamento',
		}),
	);

	const handleOnPayment = (session: Sessions.Session) => {
		const encodedParam = encodeUrl({ s: 3, session: session.id });
		navigate(`/consulta?d=${encodedParam}`);
	};

	const handleOnSessionReturn = (session: Sessions.Session) => {
		setConfirmText('Criar retorno');
		setCancelText('Cancelar');
		openModal({
			content: (
				<ReturnSessionModal
					professional={session.professional}
					setSelectedReturnDate={setSelectedDate}
				/>
			),
			title: (
				<div className="gap-2 md:flex">
					<h3 className="text-base font-normal">
						Marcar retorno com
					</h3>
					<h3>{formatProfessionalFullName(session.professional)}</h3>
				</div>
			),
		});
		setOnConfirm(() => {
			mutateCreate({
				professional_id: session.professional.id,
				type: 'RETURN',
				parent_session: session.id,
				service_id: session.service_id,
				...selectedReturnDateRef.current,
			});
		});
	};

	const userData = getUserData();

	const sessionsList = sessions?.data ?? [];
	const sortedSessionsList =
		activeTab === 'next'
			? [...sessionsList].sort((a, b) => {
					const aTime = new Date(a.selected_date).getTime();
					const bTime = new Date(b.selected_date).getTime();
					return aTime - bTime;
			  })
			: sessionsList;

	const slidingTabsDefault = openFiltersTab ? 'all' : 'next';

	return (
		<section
			data-site-tutorial="home-root"
			className="flex min-h-screen w-full items-start justify-center bg-[#f8f9fa] px-3 py-4 md:px-4 md:py-5"
		>
			<div className="animate-in fade-in flex w-full max-w-[1280px] flex-col gap-6 pb-16 duration-500">
				<div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
					<SubpageHeader
						title="Minhas consultas"
						onBack={() => navigate('/home')}
					/>
					<div
						data-site-tutorial="home-schedule-button"
						className="shrink-0"
					>
						<Button
							text="Agendar"
							data-testid={E2E_USER_HOME.SCHEDULE_BUTTON}
							className="rounded-full bg-emerald-600 px-5 py-2 font-black uppercase text-white"
							startIcon={<Plus className="size-4" />}
							onClick={() =>
								navigate(`/consulta?${searchParams.toString()}`)
							}
						/>
					</div>
				</div>
				<SlidingTabs
					tabs={tabs}
					defaultActiveTab={slidingTabsDefault}
					onChange={(tabId) => setActiveTab(tabId)}
				>
					{() => (
						<div
							className={`flex h-full w-full flex-col space-y-8 ${
								activeTabConfig?.id === 'all'
									? 'pb-20 md:pb-16'
									: 'pb-6 md:pb-0'
							}`}
							data-site-tutorial="home-next-panel"
							ref={sessionsContainerRef}
						>
							{activeTabConfig?.id === 'all' && (
								<div
									data-site-tutorial="home-filters-options"
									className="w-full rounded-[2.5rem] border border-zinc-100 bg-white p-6 shadow-sm md:p-8"
								>
									<div className="space-y-4">
										<h3 className="text-[10px] font-black uppercase tracking-[0.16em] text-stone-400">
											Data
										</h3>
										<DateRangePicker
											onChange={handleDateChange}
											clearable={clearDateFilter}
										/>
									</div>
									<div className="my-6 border-t border-zinc-100" />
									<div className="space-y-4">
										<h3 className="text-[10px] font-black uppercase tracking-[0.16em] text-stone-400">
											Status
										</h3>
										<RadioButtonGroup
											options={allFilters}
											value={allFilter}
											onChange={handleStatusChange}
											name="allFilter"
											className="w-full flex-wrap justify-start gap-2 sm:gap-3"
										/>
									</div>
								</div>
							)}
							<div className="grid gap-4">
								{isLoading ? (
									<SessionCardSkeleton />
								) : sessionsList.length === 0 ? (
									<div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 py-16 text-center">
										<div className="mb-3 rounded-full bg-white p-4 text-stone-300 shadow-sm">
											<Calendar size={28} />
										</div>
										<p className="text-sm font-bold text-stone-600">
											Nenhuma sessão por enquanto
										</p>
										<p className="mt-1 text-xs text-stone-400">
											Quando você agendar uma consulta,
											ela aparecerá aqui.
										</p>
										<Button
											text="Agendar"
											data-testid={
												E2E_USER_HOME.SCHEDULE_BUTTON_EMPTY
											}
											variant="outline"
											className="mt-4 rounded-full border-stone-300 px-5 py-2 font-bold"
											startIcon={
												<Plus className="size-4 stroke-[3]" />
											}
											onClick={() =>
												navigate(
													`/consulta?${searchParams.toString()}`,
												)
											}
										/>
									</div>
								) : (
									sortedSessionsList.map((session) => (
										<SessionCard
											key={session.id}
											session={session}
											highlightBenefitPayment
											isProfessionalView={false}
											currentUserId={userData?.id}
											handleOnPayment={handleOnPayment}
											handleOnReturn={
												handleOnSessionReturn
											}
										/>
									))
								)}
							</div>
							{sessions && sessions.total > limit && (
								<DataTablePagination
									onPageChange={handlePageChange}
									page={page}
									totalItems={sessions.total}
									pageSize={limit}
								/>
							)}
						</div>
					)}
				</SlidingTabs>
			</div>
		</section>
	);
};

export { UserMinhasConsultas };
